//@ts-nocheck

import styled, { css } from "styled-components";
import { Icon } from "../Icon/Icon.styles";
import { ButtonLabel } from "../Typography/Typography.styles";

export const ResetButton = styled.button`
  width: auto;
  position: relative;
  margin: 0;
  cursor: pointer;
  text-align: inherit;
  line-height: normal;
  overflow: hidden;
  -webkit-font-smoothing: inherit;
  -moz-osx-font-smoothing: inherit;
  appearance: none;
  -webkit-appearance: none;
  text-decoration: none;
`;

const primaryHoverFocusActiveState = css`
  transform: ${({ theme, iconStart, iconEnd, $label }) => theme.styledVariant('variant', {
    default: (iconStart || iconEnd) && !$label ? null : 'translateY(-2px)',
    primary: (iconStart || iconEnd) && !$label ? null : 'translateY(-2px)',
    secondary: (iconStart || iconEnd) && !$label ? null : 'translateY(-2px)',
    tertiary: (iconStart || iconEnd) && !$label ? null : 'translateY(-2px)',
    quaternary: (iconStart || iconEnd) && !$label ? null : 'translateY(0)',
    algolia: (iconStart || iconEnd) && !$label ? null : 'translateY(-2px)',
  })};
  transform: none;
`;

export const StyledUnderline = styled.span`
  width: 0;
  height: ${({ theme }) => theme.rem(10)};
  display: inline-flex;
  position: absolute;
  bottom: 0;
  left: ${({ theme }) => theme.rem(8)};
  pointer-events: none;
  transition: width 0.6s cubic-bezier(1, 0, 0, 1);

  svg {
    stroke-width: 2;
  }
`

export const StyledButton = styled(ResetButton)`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  min-height: ${({ theme }) => theme.rem(46)};
  min-width: ${({ theme }) => theme.rem(46)};

  border: ${({ theme }) => theme.styledVariant('variant', {
    default: '1px solid',
    primary: '1px solid',
    secondary: '1px solid',
    tertiary: '1px solid transparent',
    quaternary: 'none',
    algolia: '1px solid',
  })};

  background-color: ${({ theme, colorScheme }) => theme.styledVariant('variant', {
    primary: theme.colorScheme[colorScheme].button.primary.bgDefault,
    secondary: theme.colorScheme[colorScheme].button.secondary.bgDefault,
    tertiary: theme.colorScheme[colorScheme].button.tertiary.bgDefault,
    quaternary: 'transparent',
    algolia: 'transparent',
  })};

  border-color: ${({ theme, colorScheme }) => theme.styledVariant('variant', {
    primary: theme.colorScheme[colorScheme].button.primary.borderDefault,
    secondary: theme.colorScheme[colorScheme].button.secondary.borderDefault,
    tertiary: theme.colorScheme[colorScheme].button.tertiary.borderDefault,
    quaternary: 'transparent',
    algolia: 'transparent',
  })};
  
  color: ${({ theme, colorScheme }) => theme.styledVariant('variant', {
    primary: theme.colorScheme[colorScheme].button.primary.textDefault,
    secondary: theme.colorScheme[colorScheme].button.secondary.textDefault,
    tertiary: theme.colorScheme[colorScheme].button.tertiary.textDefault,
    quaternary: theme.colorScheme[colorScheme].button.secondary.textDefault,
    algolia: theme.colorScheme[colorScheme].button.secondary.textDefault,
  })};

  border-radius: ${({ theme }) => theme.styledVariant('variant', {
    default: theme.rem(4),
  })};

  padding: ${({ theme }) => theme.rem(0, 16)};

  ${({ $label, iconStart }) => iconStart && $label && css`
    padding-left: 0;
  `}

  ${({ noHorizontalPadding }) => noHorizontalPadding && css`
    padding-left: 0;
    padding-right: 0;
  `}

  outline: ${({ theme }) => theme.styledVariant('variant', {
    default: '2px solid transparent',
    primary: '2px solid transparent',
    secondary: '2px solid transparent',
    tertiary: 'none',
    algolia: '2px solid transparent',
  })};

  outline-offset: ${({ theme }) => theme.styledVariant('variant', {
    default: '2px',
    primary: '2px',
    secondary: '2px',
    tertiary: 'unset',
    algolia: '2px',
  })};

  ${StyledUnderline} {
    stroke: ${({ theme, colorScheme }) => theme.styledVariant('variant', {
      default: theme.colorScheme[colorScheme].button.primary.borderDefault,
      primary: theme.colorScheme[colorScheme].button.primary.borderDefault,
      secondary: theme.colorScheme[colorScheme].button.secondary.borderDefault,
      tertiary: theme.colorScheme[colorScheme].button.tertiary.borderDefault,
    })};
  }

  ${({ variant }) => variant == 'quaternary' && css`
    &:after {
      position: absolute;
      bottom: 0;
      left: 0;

      opacity: 0.75;
      width: 100%;
      height: 2px;

      content: "";
      transition: transform 0.2s;
      transform: scale(0, 1);
      transform-origin: 100% 50%;

      background-color: ${({ theme, colorScheme }) => theme.colorScheme[colorScheme].button.primary.textHover};
    }
  `}

  box-shadow: 0 0 0 0;
  transform: translateY(0);
  transition:
    background 0.3s ease-in-out, 
    color 0.3s ease-in-out,
    border-color 0.3s ease-in-out,
    outline 0.3s ease-in-out,
    padding 0.6s ease-in-out,
    transform 0.3s cubic-bezier(0.5, 2.5, 0.7, 0.7),
    box-shadow 0.3s cubic-bezier(0.5, 2.5, 0.7, 0.7);

  ${({ round, theme }) => round && css`
    width: ${theme.rem(48)};
    height: ${theme.rem(48)};
    border-radius: 50%;
    
    @media screen and (min-width: ${theme.breakpoints.lg}px) {
      width: ${theme.rem(84)};
      height: ${theme.rem(84)};
    }
  `}

  &:not([disabled]),
  &:not([aria-disabled="true"]) {
    // HOVER
    @media screen and (min-width: ${({ theme }) => theme.breakpoints.lg}px) {
      &:hover {
        color: ${({ theme, colorScheme }) => theme.styledVariant('variant', {
          primary: theme.colorScheme[colorScheme].button.primary.textHover,
          secondary: theme.colorScheme[colorScheme].button.secondary.textHover,
          tertiary: theme.colorScheme[colorScheme].button.tertiary.textHover,
          quaternary: theme.colorScheme[colorScheme].button.primary.textHover,
          algolia: theme.colorScheme[colorScheme].button.primary.textHover,
        })};

        background-color: ${({ theme, colorScheme }) => theme.styledVariant('variant', {
          primary: theme.colorScheme[colorScheme].button.primary.bgHover,
          secondary: theme.colorScheme[colorScheme].button.secondary.bgHover,
          tertiary: theme.colorScheme[colorScheme].button.tertiary.bgHover,
          quaternary: 'transparent',
          algolia: theme.colorScheme[colorScheme].button.primary.bgHover,
        })};

        border-color: ${({ theme, colorScheme }) => theme.styledVariant('variant', {
          primary: theme.colorScheme[colorScheme].button.primary.borderHover, 
          secondary: theme.colorScheme[colorScheme].button.secondary.borderHover,
          tertiary: theme.colorScheme[colorScheme].button.tertiary.borderHover,
          quaternary: 'transparent',
          algolia: 'transparent',
        })};

        ${({ variant }) => variant == 'quaternary' && css`
          &:after {
            transform: scale(1);
            transform-origin: 0 50%;
          }
        `}

        & ${Icon} {
          color: ${({ theme, colorScheme }) => theme.styledVariant('variant', {
            primary: theme.colorScheme[colorScheme].button.primary.iconHover,
            secondary: theme.colorScheme[colorScheme].button.secondary.iconHover,
            tertiary: theme.colorScheme[colorScheme].button.tertiary.iconHover,
            quaternary: theme.colorScheme[colorScheme].button.primary.iconHover,
            algolia: 'inherit',
          })};
        }

        ${primaryHoverFocusActiveState}
      }
    }
  
    // FOCUS
    &:focus-visible {
      ${({ theme, colorScheme }) => css`
        color: ${theme.styledVariant('variant', {
          default: theme.colorScheme[colorScheme].button.primary.textHover,
          primary: theme.colorScheme[colorScheme].button.primary.textHover,
          secondary: theme.colorScheme[colorScheme].button.secondary.textHover,
          tertiary: theme.colorScheme[colorScheme].button.tertiary.textHover,
        })};
        border-color: ${({ theme, colorScheme }) => theme.styledVariant('variant', {
          default: theme.colorScheme[colorScheme].button.primary.borderHover,
          primary: theme.colorScheme[colorScheme].button.primary.borderHover,
          secondary: theme.colorScheme[colorScheme].button.secondary.borderHover,
          tertiary: theme.colorScheme[colorScheme].button.tertiary.borderHover,
        })};
        outline-color: ${({ theme, colorScheme }) => theme.styledVariant('variant', {
          default: theme.colorScheme[colorScheme].button.primary.borderHover,
          primary: theme.colorScheme[colorScheme].button.primary.borderHover,
          secondary: theme.colorScheme[colorScheme].button.secondary.borderHover,
          tertiary: theme.colorScheme[colorScheme].button.tertiary.borderHover,
        })};
        background-color: ${({ theme, colorScheme }) => theme.styledVariant('variant', {
          default: theme.colorScheme[colorScheme].button.primary.bgHover,
          primary: theme.colorScheme[colorScheme].button.primary.bgHover,
          secondary: theme.colorScheme[colorScheme].button.secondary.bgHover,
          tertiary: theme.colorScheme[colorScheme].button.tertiary.bgHover,
        })};
      `};
      
      & ${Icon} {
        color: ${({ theme, colorScheme }) => theme.styledVariant('variant', {
          primary: theme.colorScheme[colorScheme].button.primary.iconHover,
          secondary: theme.colorScheme[colorScheme].button.secondary.iconHover,
          tertiary: theme.colorScheme[colorScheme].button.tertiary.iconHover,
          quaternary: theme.colorScheme[colorScheme].button.primary.iconHover,
          algolia: 'inherit',
        })};
      }

      ${primaryHoverFocusActiveState}
    }
  
    // ACTIVE and ARIA CURRENT
    &:active,
    &[aria-current="true"] {
      background-color: ${({ theme, colorScheme }) => theme.styledVariant('variant', {
        default: theme.colorScheme[colorScheme].button.primary.bgPressed,
        primary: theme.colorScheme[colorScheme].button.primary.bgPressed,
        secondary: theme.colorScheme[colorScheme].button.secondary.bgPressed,
        tertiary: theme.colorScheme[colorScheme].button.tertiary.bgPressed,
      })};

      color: ${({ theme, colorScheme }) => theme.styledVariant('variant', {
        default: theme.colorScheme[colorScheme].button.primary.textPressed,
        primary: theme.colorScheme[colorScheme].button.primary.textPressed,
        secondary: theme.colorScheme[colorScheme].button.secondary.textPressed,
        tertiary: theme.colorScheme[colorScheme].button.tertiary.textPressed,
      })};

      

      & ${Icon} {
        color: ${({ theme, colorScheme }) => theme.styledVariant('variant', {
          primary: theme.colorScheme[colorScheme].button.primary.iconPressed,
          secondary: theme.colorScheme[colorScheme].button.secondary.iconPressed,
          tertiary: theme.colorScheme[colorScheme].button.tertiary.iconPressed,
          quaternary: theme.colorScheme[colorScheme].button.primary.iconPressed,
          algolia: 'inherit',
        })};
      }

      ${primaryHoverFocusActiveState}
    }
  }

  // ARIA CURRENT
  &[aria-current="true"] {
    
  }

  // ICONS
  & > ${Icon} {
    min-width: ${({ theme }) => theme.styledVariant('variant', {
      default: theme.rem(16),
      tertiary: 'auto',
    })};
    min-height: ${({ theme }) => theme.styledVariant('variant', {
      default: theme.rem(16),
      tertiary: 'auto',
    })};

    color: ${({ theme, colorScheme }) => theme.styledVariant('variant', {
      default: theme.colorScheme[colorScheme].button.primary.iconDefault,
      primary: theme.colorScheme[colorScheme].button.primary.iconDefault,
      secondary: theme.colorScheme[colorScheme].button.secondary.iconDefault,
      tertiary: theme.colorScheme[colorScheme].button.tertiary.iconDefault,
    })};

    pointer-events: none;
    z-index: 4;

    font-size: ${({ theme }) => theme.rem(16)};

    &:first-child {
      margin-right: ${({ theme, $label, iconStart, iconEnd }) => theme.styledVariant('variant', {
        default: $label && (iconStart || iconEnd) ? theme.rem(8) : '0',
        tertiary: $label && (iconStart || iconEnd) ? theme.rem(8) : '0',
      })};
    }

    &:last-of-type {
      margin-left: ${({ theme, $label, iconStart, iconEnd }) => theme.styledVariant('variant', {
        default: $label && (iconStart || iconEnd) ? theme.rem(8) : '0',
        tertiary: $label && (iconStart || iconEnd) ? theme.rem(8) : '0',
      })};
    }
  }

  // DISABLED
  &[disabled],
  &[aria-disabled="true"] {
    pointer-events: none;
    cursor: not-allowed;
    color: ${({ theme, colorScheme }) => theme.colorScheme[colorScheme].button.primary.disabledText};
    background-color: ${({ theme, colorScheme }) => theme.colorScheme[colorScheme].button.primary.bgDisabled};
    border-color: transparent;

    padding: ${({ theme, iconStart, iconEnd, $label }) => theme.styledVariant('variant', {
      default: (iconStart || iconEnd) && !$label ? theme.rem(8) : theme.rem(8, 12),
      primary: (iconStart || iconEnd) && !$label ? theme.rem(8) : theme.rem(8, 12),
      secondary: (iconStart || iconEnd) && !$label ? theme.rem(8) : theme.rem(8, 12),
      tertiary: (iconStart || iconEnd) && !$label ? theme.rem(8) : theme.rem(8, 12),
      quaternary: (iconStart || iconEnd) && !$label ? theme.rem(8) : theme.rem(8, 12),
      algolia: (iconStart || iconEnd) && !$label ? theme.rem(8) : theme.rem(8, 12),
    })};
  }

  ${({ theme, fullWidth, variant }) => fullWidth && variant != 'tertiary' && css`
    ${Array.isArray(fullWidth)
      ? Object.entries(theme.breakpoints).map(([dim]) => theme.mediaQuery[dim] && theme.mediaQuery[dim](`
          width: ${fullWidth.indexOf(dim) !== -1 ? '100%' : 'auto'};
        `))
      : 'width: 100%;'}
  `}

  // VARIANT FULL WIDTH - SECONDARY
  ${({ theme, fullWidth, variant }) => fullWidth && variant == 'tertiary' && css`
    ${Array.isArray(fullWidth)
      ? Object.entries(theme.breakpoints).map(([dim]) => theme.mediaQuery[dim] && theme.mediaQuery[dim](
          fullWidth.indexOf(dim) !== -1 ? `padding-left: ${theme.rem(8)};` : null
        ))
      : `padding-left: ${theme.rem(8)};`}
  `}

  // IS ACTIVE
  ${({ isActive }) => isActive && css`
    @media screen and (min-width: ${({ theme }) => theme.breakpoints.lg}px) {
      color: ${({ theme, colorScheme }) => theme.styledVariant('variant', {
        default: theme.colorScheme[colorScheme].button.primary.textHover,
        primary: theme.colorScheme[colorScheme].button.primary.textHover,
        secondary: theme.colorScheme[colorScheme].button.secondary.textHover,
        tertiary: theme.colorScheme[colorScheme].button.tertiary.textHover,
      })};

      background-color: ${({ theme, colorScheme }) => theme.styledVariant('variant', {
        default: theme.colorScheme[colorScheme].button.primary.bgHover,
        primary: theme.colorScheme[colorScheme].button.primary.bgHover,
        secondary: theme.colorScheme[colorScheme].button.secondary.bgHover,
        tertiary: theme.colorScheme[colorScheme].button.tertiary.bgHover,
      })};

      border-color: ${({ theme, colorScheme }) => theme.styledVariant('variant', {
        default: theme.colorScheme[colorScheme].button.primary.borderHover,
        primary: theme.colorScheme[colorScheme].button.primary.borderHover,
        secondary: theme.colorScheme[colorScheme].button.secondary.borderHover,
        tertiary: theme.colorScheme[colorScheme].button.tertiary.borderHover,
      })};

      & ${Icon} {
        color: ${({ theme, colorScheme }) => theme.styledVariant('variant', {
          primary: theme.colorScheme[colorScheme].button.primary.iconPressed,
          secondary: theme.colorScheme[colorScheme].button.secondary.iconPressed,
          tertiary: theme.colorScheme[colorScheme].button.tertiary.iconPressed,
          quaternary: theme.colorScheme[colorScheme].button.primary.iconPressed,
          algolia: 'inherit',
        })};
      }

      ${primaryHoverFocusActiveState}
    }
  `}
`;
